import { FolderPlus } from 'lucide-react';
import { CompactSearchBar } from '@/components/features/learning/search/CompactSearchBar';
import { FolderBreadcrumbs } from './FolderBreadcrumbs';
import { TagFilter } from './TagFilter';
import { ViewFilter, type ViewFilterValue } from './ViewFilter';
import type { BreadcrumbItem } from '@/lib/api-client';

export interface MyLearningHeaderProps {
  /** `'My Learning'` at root, otherwise the current folder's name. */
  title: string;
  /** Root swaps the breadcrumb trail for the dashboard subtitle. */
  isRoot: boolean;
  breadcrumbs: BreadcrumbItem[];
  onNavigateFolder: (folderId: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;
  /** Total across the whole filtered stream, not just this page. */
  totalResults?: number;
  isLoading?: boolean;
  viewFilter: ViewFilterValue;
  onViewFilterChange: (value: ViewFilterValue) => void;
  tagFilter: string[];
  onTagFilterChange: (value: string[]) => void;
  onNewFolder: () => void;
}

/**
 * The pinned header: title, folder trail, search, both filters, and the
 * new-folder button.
 *
 * Two arrangements of the same controls. From `md` it is one row - title
 * left, tools right. Below `md` it stacks: title + new folder, full-width
 * search, then the filter chips. Breadcrumbs collapse to a "back to parent"
 * link there (`FolderBreadcrumbs`). The one-row form at phone width was 632px
 * wide on a 390px screen: the search box sat on top of the title and the
 * filters and new-folder button were off-screen, unreachable.
 *
 * The filter row is `relative` on a phone because both filter popovers
 * anchor to it there (their triggers go `static`), so a popover spans the
 * row instead of running off the screen's edge from a small chip.
 *
 * `flex-shrink-0` is load-bearing — the page is a flex column whose grid area
 * scrolls, and without it this header compresses as the grid grows.
 *
 * Both filters take `disabled` from the same `isLoading` flag rather than
 * their own, so a refetch cannot leave one interactive and the other not.
 */
export function MyLearningHeader({
  title,
  isRoot,
  breadcrumbs,
  onNavigateFolder,
  search,
  onSearchChange,
  onSearchClear,
  totalResults,
  isLoading,
  viewFilter,
  onViewFilterChange,
  tagFilter,
  onTagFilterChange,
  onNewFolder,
}: MyLearningHeaderProps) {
  return (
    // A grid, so each control keeps ONE element and moves between the two
    // arrangements by placement alone. Phone: title + new folder / search /
    // filters, three rows. `md`: all four in one row.
    <div className='mb-4 grid flex-shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3 md:mb-8 md:grid-cols-[minmax(0,1fr)_auto_auto_auto] md:gap-x-2 md:gap-y-0'>
      <div className='min-w-0 md:mr-4'>
        <h1 className='mb-1 truncate text-2xl font-bold text-foreground md:mb-2 md:text-3xl'>
          {title}
        </h1>
        {isRoot ? (
          <p className='hidden text-muted-foreground md:block'>
            Your personalized learning dashboard
          </p>
        ) : (
          <FolderBreadcrumbs
            breadcrumbs={breadcrumbs}
            onNavigate={onNavigateFolder}
          />
        )}
      </div>

      <div className='col-span-2 row-start-2 md:col-span-1 md:col-start-2 md:row-start-1 md:w-[24rem]'>
        <CompactSearchBar
          value={search}
          onChange={onSearchChange}
          onClear={onSearchClear}
          placeholder={isRoot ? 'Search lessons...' : 'Search in folder...'}
          totalResults={totalResults}
          isLoading={isLoading}
          showResultsCounter={false}
        />
      </div>

      <div className='relative col-span-2 row-start-3 flex items-center gap-2 md:static md:col-span-1 md:col-start-3 md:row-start-1'>
        <ViewFilter
          value={viewFilter}
          onChange={onViewFilterChange}
          disabled={isLoading}
        />
        <TagFilter
          value={tagFilter}
          onChange={onTagFilterChange}
          disabled={isLoading}
        />
      </div>

      <button
        type='button'
        onClick={onNewFolder}
        aria-label='New folder'
        title='New folder'
        className='col-start-2 row-start-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:h-10 sm:w-10 md:col-start-4'
      >
        <FolderPlus className='h-4 w-4' />
      </button>
    </div>
  );
}
